import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { DirectionProvider } from "@radix-ui/react-direction";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { makeT } from "@/i18n/strings";

const LangCtx = createContext(null);
const AuthCtx = createContext(null);

export function AdminLangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem("wt_admin_lang") === "ar" ? "ar" : "en";
    } catch {
      return "en";
    }
  });
  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      localStorage.setItem("wt_admin_lang", l);
    } catch {}
  }, []);
  const value = useMemo(() => ({ lang, setLang, t: makeT(lang), dir: lang === "ar" ? "rtl" : "ltr" }), [lang, setLang]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useAdminLang = () => useContext(LangCtx);

export function AdminFrame({ children }) {
  const { lang, dir } = useAdminLang();
  useLayoutEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);
  return (
    <DirectionProvider dir={dir}>
      <div dir={dir} lang={lang} className="admin-root min-h-screen bg-background text-foreground">
        {children}
      </div>
    </DirectionProvider>
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    const off = () => setUser(false);
    window.addEventListener("wt-unauthorized", off);
    return () => window.removeEventListener("wt-unauthorized", off);
  }, []);
  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
  }, []);
  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  }, []);
  const value = useMemo(() => ({ user, login, logout }), [user, login, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export const FullLoader = () => (
  <div className="grid min-h-screen place-items-center" data-testid="admin-loading">
    <Loader2 className="h-6 w-6 animate-spin text-primary" />
  </div>
);

export function RequireAdmin({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: loc.pathname }} />;
  return children;
}
